import React from "react";
import cn from "classnames";
import {
  TicketFilterTab,
  useTicketFilterNavigation,
} from "./use-pill-filtering";
import styles from "./pill-filtering.module.scss";
import { SupportAgentTicket } from "@src/types/support";

interface TicketFilterPillNavigationProps<TFilter extends string = string> {
  /**
   * Tab definitions. Each tab declares its display label, the filter key it
   * emits, and an optional function that computes its badge count from the
   * tickets array.
   *
   * @example
   * const TABS = [
   *   { label: "All",        filterKey: "all" },
   *   { label: "Unassigned", filterKey: "unassigned", countFn: (t) => t.filter(x => x.status === "UNASSIGNED").length },
   *   { label: "My Tickets", filterKey: "my_tickets",  countFn: (t) => t.filter(x => x.assigned_to.id === currentUserId).length },
   *   { label: "Resolved",   filterKey: "resolved" },
   * ] satisfies TicketFilterTab<MyFilter>[];
   */
  tabs: TicketFilterTab<TFilter>[];

  /**
   * The currently active filter key — lets the parent drive selection.
   * Changing this prop will smoothly animate the pill to the matching tab.
   */
  activeFilter: TFilter;

  /**
   * Called immediately on every click with the tab's filterKey.
   * The parent is responsible for updating its own state and re-rendering.
   */
  onFilter: (filter: TFilter) => void;

  /**
   * The full (unfiltered) ticket dataset used to compute badge counts via
   * each tab's `countFn`. Updating this array re-runs all countFns
   * automatically — no extra wiring needed.
   */
  tickets: SupportAgentTicket[];

  /** Additional class name forwarded to the root element. */
  className?: string;
}

const cssClassName = "pill-filtering";

export const PillFiltering = <TFilter extends string = string>({
  tabs,
  activeFilter,
  onFilter,
  tickets,
  className,
}: TicketFilterPillNavigationProps<TFilter>) => {
  const {
    containerRef,
    buttonRefs,
    backgroundStyle,
    slidingTransition,
    selectedIndex,
    badgeCounts,
    flexGrowValues,
    handleClick,
  } = useTicketFilterNavigation({ tabs, activeFilter, onFilter, tickets });

  return (
    <div
      className={cn(styles[cssClassName], className)}
      ref={containerRef}
      role="tablist"
      aria-label="Ticket filter"
    >
      <div
        className={styles[`${cssClassName}__selected-background`]}
        style={{
          width: `${backgroundStyle.width}px`,
          left: `${backgroundStyle.left}px`,
          transition: slidingTransition,
        }}
        aria-hidden="true"
      />

      {tabs.map((tab, index) => {
        const count = badgeCounts[index];
        const hasBadge = typeof count === "number" && count > 0;
        const isSelected = selectedIndex === index;

        return (
          <button
            key={tab.filterKey}
            ref={(el) => (buttonRefs.current[index] = el)}
            role="tab"
            aria-selected={isSelected}
            style={{ flexGrow: flexGrowValues[index] ?? 1 }}
            className={cn(styles[`${cssClassName}__button`], {
              [styles[`${cssClassName}__button--selected`]]: isSelected,
            })}
            onClick={() => handleClick(index, tab.filterKey)}
          >
            {tab.label}

            {hasBadge && (
              <span
                className={cn(styles[`${cssClassName}__badge`], {
                  [styles[`${cssClassName}__badge--wide`]]: count > 9,
                })}
                aria-label={`${count} tickets`}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};
